import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, Store, DollarSign, User, Menu, X, LogOut, 
  Bell, ChevronDown, Copy, Check, LifeBuoy, Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const menuItems = [
  { path: '/sales', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/sales/restaurants', icon: Store, label: 'My Restaurants' },
  { path: '/sales/earnings', icon: DollarSign, label: 'Earnings' },
  { path: '/sales/support', icon: LifeBuoy, label: 'Support' },
  { path: '/sales/profile', icon: User, label: 'Profile' },
];

export default function SalesLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const copyReferralCode = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white shadow-xl transform transition-transform lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b">
            <Link to="/sales" className="flex items-center gap-2">
              <span className="text-2xl font-bold text-orange-500">Hambre</span>
              <span className="text-sm text-slate-500 bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Sales</span>
            </Link>
          </div>

          {/* User info with referral code */}
          <div className="p-4 border-b bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-sm text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            {user?.referral_code && (
              <div className="mt-3 p-2 bg-white rounded-lg border">
                <p className="text-xs text-slate-500 mb-1">Your Referral Code</p>
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono font-bold text-purple-600">{user.referral_code}</code>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={copyReferralCode}
                    className="h-7 px-2"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-2">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive 
                          ? 'bg-purple-100 text-purple-700 font-medium' 
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Logout */}
          <div className="p-4 border-t">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full text-slate-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-4">
              <button 
                onClick={() => toast.info('Notifications coming soon!')}
                className="p-2 rounded-lg hover:bg-slate-100 relative"
              >
                <Bell className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
